/**
 * Sprint 24.5 — first-party connector template hooks.
 *
 * Co-located with use-connections.ts (the connections domain lives in
 * console/src/hooks/, not console/src/v3/hooks/) so the wizard, Add-menu cards,
 * and brand lookups share one source. The worker owns the registry
 * (src/engine/first-party-templates.ts); the console only ever sees the
 * dev-mode-filtered list returned by `list_first_party_templates`, so the shape
 * below mirrors the worker's FirstPartyTemplate.
 */

import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../lib/mcp-client";

export type WizardStepId =
  | "confirm"
  | "authorize"
  | "toolsets"
  | "success"
  | "url-input"
  | "test";

export interface FirstPartyTemplateToolset {
  id: string;
  name: string;
  icon: string;
  tool_count_estimate: number;
  default_enabled: boolean;
}

export interface FirstPartyTemplate {
  slug: string;
  display_name: string;
  description: string;
  brand: {
    avatar_bg: string;
    avatar_color: string;
    icon_name: string;
    accent_border: string;
  };
  type: "mcp" | "wrapped";
  connection_template: {
    url?: string;
    transport?: string;
    auth_type?: string;
    oauth_provider?: string;
    oauth_scopes?: string;
    oauth_authorize_url?: string;
    oauth_token_url?: string;
    spec_url?: string;
    auth_kind?: string;
  };
  wizard_steps: WizardStepId[];
  toolsets?: FirstPartyTemplateToolset[];
  capability_footer: string;
  status: "live" | "coming-soon" | "dev-only";
  enabled: boolean;
  dev_only: boolean;
}

/** The first-party templates available to this workspace (dev-mode filtered server-side). */
export function useFirstPartyTemplates() {
  return useQuery({
    queryKey: ["first-party-templates"],
    queryFn: async (): Promise<FirstPartyTemplate[]> => {
      const result = await mcpClient.toolsCall<{ templates: FirstPartyTemplate[] }>(
        "list_first_party_templates",
        {},
      );
      if (result.isError) throw new Error("Failed to fetch first-party templates");
      return (result.structuredContent as { templates: FirstPartyTemplate[] })?.templates ?? [];
    },
    // Registry is in-code and changes only on deploy — cache generously.
    staleTime: 5 * 60_000,
  });
}

/**
 * A `slug → template` lookup for brand resolution on connection rows + the MCP
 * detail page. Returns an empty map while loading or if the registry can't be
 * resolved (callers fall back to default branding — Sprint 24.5 S24.5.32).
 */
export function useFirstPartyTemplateMap(): {
  map: Record<string, FirstPartyTemplate>;
  isLoading: boolean;
} {
  const { data, isLoading } = useFirstPartyTemplates();
  const map = useMemo(() => {
    const m: Record<string, FirstPartyTemplate> = {};
    for (const t of data ?? []) m[t.slug] = t;
    return m;
  }, [data]);
  return { map, isLoading };
}

export interface RegisterFromTemplateResult {
  connection_kind: "mcp" | "wrapped";
  connection_id: string | null;
  template_slug: string;
  /** The freshly-created connection (MCP `toPublicConnection` shape) when available. */
  connection?: { connection_id?: string; status?: string; tool_count?: number } | null;
}

export interface RegisterFromTemplateInput {
  template_slug: string;
  name?: string;
  url?: string;
  enabled_toolsets?: string[];
}

/** Provision a connection from a template (owner-only on the worker side). */
export function useRegisterFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RegisterFromTemplateInput): Promise<RegisterFromTemplateResult> => {
      const result = await mcpClient.toolsCall<RegisterFromTemplateResult>(
        "register_from_template",
        input as unknown as Record<string, unknown>,
      );
      if (result.isError) {
        const data = (result.structuredContent ?? {}) as { error?: string };
        const block = result.content?.[0] as { text?: string } | undefined;
        throw new Error(data.error ?? block?.text ?? "Failed to register from template");
      }
      return result.structuredContent as RegisterFromTemplateResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections-unified"] });
      qc.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}

export interface SubscribeTemplateResult {
  subscribed: boolean;
  already_subscribed: boolean;
}

/** Notify-me capture for COMING SOON templates. Idempotent per user (Q2). */
export function useSubscribeTemplateNotification() {
  return useMutation({
    mutationFn: async (input: {
      template_slug: string;
      email?: string;
    }): Promise<SubscribeTemplateResult> => {
      const result = await mcpClient.toolsCall<SubscribeTemplateResult>(
        "subscribe_template_notification",
        input,
      );
      if (result.isError) throw new Error("Failed to subscribe");
      return (result.structuredContent as SubscribeTemplateResult) ?? {
        subscribed: false,
        already_subscribed: false,
      };
    },
  });
}
